import { useEffect, useMemo, useRef, useState } from 'react'
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, X } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import { Modal, Spinner } from '@/components/ds2'
import GoogleMapsLoader from '@/components/GoogleMapsLoader'
import { geocodeCityState } from '@/components/geocode'
import { LocationInputCityState } from 'features/load-hub/components/filters/LocationInputCityState'
import { LocationData } from 'features/load-hub/types/filters'
import { cityLabel } from '../utils/format'
import { LatLng, toLatLng } from './NearbyDriverRouteMap'
import { BoxTruckLoadMapEditorMap, BtRouteSummary } from './BoxTruckLoadMapEditorMap'
import { ROUTE_PIN_DESTINATION_COLOR, ROUTE_PIN_ORIGIN_COLOR } from '@/constants/mapPins'

const MAX_STOPS = 3

type Role = 'origin' | 'destination' | 'stop'
interface RoutePoint {
    id: string
    role: Role
    loc: LocationData | null
}

/** LatLng from a point's LocationData, or null when it has no usable coordinates. */
const latOf = (loc: LocationData | null): LatLng | null =>
    loc && loc.latitude != null && loc.longitude != null ? { lat: loc.latitude, lng: loc.longitude } : null

const PinBadge = ({ label, color }: { label: string; color: string }) => (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: color }}>
        {label}
    </span>
)

/** One reorderable point row. The 6-dot handle is the ONLY drag source, so the city input stays interactive. */
const SortablePointRow = ({
    point,
    label,
    color,
    role,
    onChange,
    onRemove,
}: {
    point: RoutePoint
    label: string
    color: string
    role: string
    onChange: (loc: LocationData | null) => void
    onRemove: (() => void) | null
}) => {
    const sortable = useSortable({ id: point.id })
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition ?? 'transform 180ms cubic-bezier(0.2, 0, 0, 1)',
        opacity: sortable.isDragging ? 0.5 : 1,
    }
    return (
        <div ref={sortable.setNodeRef} style={style} className="rounded-lg border border-border bg-surface p-2">
            <div className="mb-1 flex items-center gap-1.5">
                <span
                    {...sortable.listeners}
                    {...sortable.attributes}
                    className="cursor-grab text-foreground-muted active:cursor-grabbing"
                    aria-label="Drag to reorder"
                >
                    <GripVertical size={14} />
                </span>
                <PinBadge label={label} color={color} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">{role}</span>
                {onRemove && (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded text-foreground-muted transition-colors hover:bg-default hover:text-danger"
                        aria-label={`Remove ${role}`}
                    >
                        <X size={12} />
                    </button>
                )}
            </div>
            <LocationInputCityState value={point.loc} onChange={onChange} placeholder="City or ZIP — or drag the pin" cityOnly insideDialog />
        </div>
    )
}

/**
 * The expanded, editable route planner for a box-truck load — a Google-Maps-style panel with a DRAG-TO-REORDER
 * list (origin, up to 3 stops, destination), each row the same city autocomplete the loadboard uses, and a
 * large map whose pins are all DRAGGABLE (route re-snaps to roads on every move). "Add stop" appends a new stop
 * at the BOTTOM of the list; the route runs origin → stops (in their listed order) → destination, so dragging a
 * stop's handle reorders its place in the route. Seeds coords from the load's DB lat/lon, else geocodes its
 * city/state (SYLECTUS loads usually lack coords). ds2 Modal with `disableTransform` (a CSS transform on a
 * Google Maps ancestor breaks tiles).
 */
export const BoxTruckLoadMapModal = ({ load, onClose }: { load: CanonicalLoad; onClose: () => void }) => {
    const [points, setPoints] = useState<RoutePoint[]>([])
    const [seeding, setSeeding] = useState(true)
    // Routes (primary + alternates) lifted from the map, and which one is selected. A fresh compute resets to 0.
    const [routes, setRoutes] = useState<BtRouteSummary[]>([])
    const [routeIndex, setRouteIndex] = useState(0)
    const idRef = useRef(0)
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

    // Seed origin/destination from DB coords, else geocode the city/state.
    useEffect(() => {
        let cancelled = false
        const buildLoc = async (
            city: string | null,
            state: string | null,
            lat: number | null | undefined,
            lon: number | null | undefined
        ): Promise<LocationData> => {
            const ll = toLatLng(lat, lon) ?? (await geocodeCityState(city, state))
            return { city: city ?? '', stateProv: state ?? '', postalCode: '', latitude: ll?.lat, longitude: ll?.lng }
        }
        const seed = async () => {
            const [origin, destination] = await Promise.all([
                buildLoc(load.pickupCity, load.pickupStateProv, load.pickupLat, load.pickupLon),
                buildLoc(load.deliveryCity, load.deliveryStateProv, load.deliveryLat, load.deliveryLon),
            ])
            if (cancelled) return
            setPoints([
                { id: `p-${idRef.current++}`, role: 'origin', loc: origin },
                { id: `p-${idRef.current++}`, role: 'destination', loc: destination },
            ])
            setSeeding(false)
        }
        void seed()
        return () => {
            cancelled = true
        }
    }, [load])

    const setLocById = (id: string, loc: LocationData | null) => setPoints((p) => p.map((pt) => (pt.id === id ? { ...pt, loc } : pt)))
    const setCoordsById = (id: string, ll: LatLng) =>
        setPoints((p) => p.map((pt) => (pt.id === id ? { ...pt, loc: { ...(pt.loc ?? {}), latitude: ll.lat, longitude: ll.lng } } : pt)))
    const removeById = (id: string) => setPoints((p) => p.filter((pt) => pt.id !== id))

    // Memoize the role slices on `points` so the derived LatLngs below keep a STABLE identity across unrelated
    // renders (e.g. keystrokes in a city input) — otherwise the map's effect (deps origin/destination/waypoints)
    // would re-run every render, rebuilding markers and firing a fresh DirectionsService request each time.
    const originPoint = useMemo(() => points.find((p) => p.role === 'origin') ?? null, [points])
    const destPoint = useMemo(() => points.find((p) => p.role === 'destination') ?? null, [points])
    const stopPoints = useMemo(() => points.filter((p) => p.role === 'stop'), [points])

    const addStop = () => {
        const o = latOf(originPoint?.loc ?? null)
        const d = latOf(destPoint?.loc ?? null)
        if (stopPoints.length >= MAX_STOPS || !o || !d) return
        // Seed the new stop near the route middle, nudged per stop so successive ones don't stack exactly.
        const mid: LocationData = {
            city: '',
            stateProv: '',
            postalCode: '',
            latitude: (o.lat + d.lat) / 2 + 0.15 * stopPoints.length,
            longitude: (o.lng + d.lng) / 2,
        }
        // Append at the BOTTOM of the list.
        setPoints((p) => [...p, { id: `p-${idRef.current++}`, role: 'stop', loc: mid }])
    }

    const onDragEnd = (e: DragEndEvent) => {
        const { active, over } = e
        if (!over || active.id === over.id) return
        setPoints((p) => {
            const from = p.findIndex((pt) => pt.id === active.id)
            const to = p.findIndex((pt) => pt.id === over.id)
            return from === -1 || to === -1 ? p : arrayMove(p, from, to)
        })
    }

    const origin = useMemo(() => latOf(originPoint?.loc ?? null), [originPoint])
    const destination = useMemo(() => latOf(destPoint?.loc ?? null), [destPoint])
    // Only stops WITH coords become waypoints; keep their ids alongside so a drag maps to the RIGHT stop even
    // when an earlier stop was cleared (index into the FILTERED list, not the raw stopPoints).
    const stopsWithCoords = useMemo(
        () => stopPoints.map((p) => ({ id: p.id, ll: latOf(p.loc) })).filter((s): s is { id: string; ll: LatLng } => !!s.ll),
        [stopPoints]
    )
    const stopLatLngs = useMemo(() => stopsWithCoords.map((s) => s.ll), [stopsWithCoords])

    // The route runs origin → stops-with-coords (in list order) → destination, so leg N connects
    // legEndpoints[N] → legEndpoints[N+1]. Stop labels use the row's displayed number (Stop 1/2/3).
    const legEndpoints = useMemo(
        () => ['Origin', ...stopsWithCoords.map((s) => `Stop ${stopPoints.findIndex((p) => p.id === s.id) + 1}`), 'Destination'],
        [stopsWithCoords, stopPoints]
    )
    // A fresh route (drag/add/remove) resets the selection to the primary; the map already re-renders index 0.
    const onRoutes = (rs: BtRouteSummary[]) => {
        setRoutes(rs)
        setRouteIndex(0)
    }
    const selectedRoute = routes[routeIndex] ?? routes[0] ?? null

    // Row → badge label/colour by ROLE (origin A green, destination B red, stops numbered blue by their order).
    const rowMeta = (pt: RoutePoint): { label: string; color: string; role: string } => {
        if (pt.role === 'origin') return { label: 'A', color: ROUTE_PIN_ORIGIN_COLOR, role: 'Origin' }
        if (pt.role === 'destination') return { label: 'B', color: ROUTE_PIN_DESTINATION_COLOR, role: 'Destination' }
        const n = stopPoints.findIndex((s) => s.id === pt.id) + 1
        return { label: String(n), color: '#2563EB', role: `Stop ${n}` }
    }

    const header = (
        <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">Route map</div>
            <div className="truncate text-xs text-foreground-muted">
                {cityLabel(load.pickupCity, load.pickupStateProv)} → {cityLabel(load.deliveryCity, load.deliveryStateProv)}
            </div>
        </div>
    )

    return (
        <Modal open onClose={onClose} disableTransform size="lg" style={{ width: '98vw', maxWidth: '98vw' }} header={header}>
            <div className="flex h-[88vh] min-h-[500px] gap-3">
                {/* Left panel — reorderable origin / destination / stops, each a city autocomplete. */}
                <div className="flex w-80 shrink-0 flex-col gap-2 overflow-y-auto pr-1">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={points.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                            {points.map((point) => {
                                const meta = rowMeta(point)
                                return (
                                    <SortablePointRow
                                        key={point.id}
                                        point={point}
                                        label={meta.label}
                                        color={meta.color}
                                        role={meta.role}
                                        onChange={(loc) => setLocById(point.id, loc)}
                                        onRemove={point.role === 'stop' ? () => removeById(point.id) : null}
                                    />
                                )
                            })}
                        </SortableContext>
                    </DndContext>

                    <button
                        type="button"
                        onClick={addStop}
                        disabled={stopPoints.length >= MAX_STOPS || !origin || !destination}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-1.5 text-xs font-medium text-foreground-muted transition-colors hover:bg-default disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Plus size={14} /> Add stop {stopPoints.length > 0 ? `(${stopPoints.length}/${MAX_STOPS})` : ''}
                    </button>

                    {/* Trip summary — total mileage + time, per-stop legs, and selectable alternate routes. */}
                    {selectedRoute && (
                        <div className="rounded-lg border border-border bg-surface p-3.5">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="text-lg font-semibold text-foreground">{selectedRoute.duration}</span>
                                <span className="text-sm text-foreground-muted tabular-nums">{selectedRoute.distance}</span>
                            </div>

                            {selectedRoute.legs.length > 1 && (
                                <div className="mt-2.5 flex flex-col gap-1.5 border-t border-border pt-2.5">
                                    {selectedRoute.legs.map((leg, i) => (
                                        <div key={i} className="flex items-center justify-between gap-2 text-[13px] text-foreground-muted">
                                            <span className="truncate">
                                                {legEndpoints[i]} → {legEndpoints[i + 1]}
                                            </span>
                                            <span className="shrink-0 tabular-nums">
                                                {leg.distance} · {leg.duration}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {routes.length > 1 && (
                                <div className="mt-2.5 flex flex-col gap-1.5 border-t border-border pt-2.5">
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">Routes</span>
                                    {routes.map((r, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => setRouteIndex(i)}
                                            className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-[13px] transition-colors ${
                                                i === routeIndex
                                                    ? 'border-accent bg-default font-medium text-foreground'
                                                    : 'border-border text-foreground-muted hover:bg-default'
                                            }`}
                                        >
                                            <span className="truncate">{r.label ? `via ${r.label}` : `Route ${i + 1}`}</span>
                                            <span className="shrink-0 tabular-nums">
                                                {r.duration} · {r.distance}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <p className="mt-auto text-[11px] leading-snug text-foreground-muted">
                        Drag the 6-dot handle to reorder · drag any pin on the map to reposition it — the route re-routes automatically.
                    </p>
                </div>

                {/* Map */}
                <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-border">
                    {seeding ? (
                        <div className="flex h-full items-center justify-center gap-2 text-sm text-foreground-muted">
                            <Spinner color="accent" size={20} /> Locating the route…
                        </div>
                    ) : origin && destination ? (
                        <GoogleMapsLoader>
                            <BoxTruckLoadMapEditorMap
                                origin={origin}
                                destination={destination}
                                waypoints={stopLatLngs}
                                routeIndex={routeIndex}
                                onOriginChange={(ll) => originPoint && setCoordsById(originPoint.id, ll)}
                                onDestinationChange={(ll) => destPoint && setCoordsById(destPoint.id, ll)}
                                onWaypointChange={(i, ll) => stopsWithCoords[i] && setCoordsById(stopsWithCoords[i].id, ll)}
                                onRoutes={onRoutes}
                                onSelectRoute={setRouteIndex}
                            />
                        </GoogleMapsLoader>
                    ) : (
                        <div className="flex h-full items-center justify-center px-4 text-center text-sm text-foreground-muted">
                            Couldn’t resolve coordinates for this load’s origin or destination.
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
